import * as Y from "yjs";
import type { TextPath } from "./records";

/** The one shared text in each text's document. */
const TEXT = "text";

/** Bytes as base64, to carry in a JSON frame. */
export function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let start = 0; start < bytes.length; start += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(start, start + 0x8000));
  }
  return btoa(binary);
}

export function fromBase64(text: string): Uint8Array {
  const binary = atob(text);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

/**
 * Several updates to one text as one, without the text itself: what a store that never reads the
 * text keeps of it.
 */
export function mergeTextUpdates(updates: readonly Uint8Array[]): Uint8Array {
  return Y.mergeUpdates([...updates]);
}

/** Whether bytes are a Yjs update that can be merged. */
export function isTextUpdate(bytes: Uint8Array): boolean {
  try {
    Y.decodeUpdate(bytes);
    return true;
  } catch {
    return false;
  }
}

/** A text the draft saves, or sends an editor catching up, as its Yjs state. */
export interface SavedText {
  key: string;
  path: TextPath;
  generation: number;
  state: Uint8Array;
}

/**
 * A draft's shared texts, one per record and text path, each a Yjs document. A text begins from
 * the string its field held when it was last written whole, inserted by client 0, so every copy of
 * it begins the same and everyone's typing merges into one text.
 */
export class TextDocs {
  private readonly docs = new Map<
    string,
    { key: string; path: TextPath; generation: number; doc: Y.Doc }
  >();

  /** `clientId` is the Yjs client an editor types as. A draft session never types. */
  constructor(private readonly clientId?: number) {}

  /** The text at a path, begun from `initial` unless it already has been in this generation. */
  docOf(key: string, path: TextPath, generation: number, initial: string): Y.Doc {
    const id = `${key}\n${path}`;
    const known = this.docs.get(id);
    if (known?.generation === generation) return known.doc;
    known?.doc.destroy();
    const doc = new Y.Doc();
    doc.clientID = 0;
    if (initial) doc.getText(TEXT).insert(0, initial);
    if (this.clientId !== undefined) doc.clientID = this.clientId;
    this.docs.set(id, { key, path, generation, doc });
    return doc;
  }

  /** Someone's typing merged into a text, and the string it holds after. */
  merge(doc: Y.Doc, update: Uint8Array): string {
    Y.applyUpdate(doc, update);
    return doc.getText(TEXT).toString();
  }

  /** A text as it was saved, so typing carries on from where it was. */
  load(saved: SavedText): void {
    const doc = new Y.Doc();
    Y.applyUpdate(doc, saved.state);
    if (this.clientId !== undefined) doc.clientID = this.clientId;
    this.docs.set(`${saved.key}\n${saved.path}`, { ...saved, doc });
  }

  /** One text's state, to save it after typing. */
  saved(key: string, path: TextPath): SavedText | null {
    const known = this.docs.get(`${key}\n${path}`);
    return known
      ? { key, path, generation: known.generation, state: Y.encodeStateAsUpdate(known.doc) }
      : null;
  }

  /** Every text anyone has typed into, for an editor catching up. */
  all(): SavedText[] {
    return [...this.docs.values()].map(({ key, path, generation, doc }) => ({
      key,
      path,
      generation,
      state: Y.encodeStateAsUpdate(doc),
    }));
  }
}

/** The shared text an editor types into, for its own edits and its own undo. */
export const sharedText = (doc: Y.Doc) => doc.getText(TEXT);
